/**
 * SSR 数据加载工具
 * 提供同构的数据获取能力，支持服务端和客户端使用相同的 fetchData 函数
 *
 * 注意：本文件同时被 webpack（客户端）和 Node（服务端）加载，
 * 不能在顶层导入 ESM 模块，否则 Node require 会失败。
 * 客户端路径通过 lazy require 延迟加载。
 *
 * 核心设计：不管是 Node 端渲染还是客户端渲染，都通过 createUnifiedContext
 * 统一创建上下文并通过 createUnifiedRequest 统一创建请求函数，
 * 保证 fetch.js 收到的参数结构完全一致。
 */

/**
 * 标准化响应格式为 [err, data]
 * 兼容不同请求库的返回值差异
 * @param {*} res - 请求返回值
 * @returns {Array} [err, data]
 */
function normalizeResponse(res) {
  if (Array.isArray(res)) {
    if (res.length === 1) return [res[0], null];
    return [res[0], res[1]];
  }
  return [null, res];
}

/**
 * 统一创建 fetch 上下文（SSR 和 CSR 共用同一函数）
 *
 * 无论服务端还是客户端，fetch.js 拿到的 context 结构完全一致：
 *   { cookies, query, params, request, state, isServer, isClient, ctx }
 *
 * - 服务端：ctx 为原始 Egg.js context（可访问 logger/service/model 等）
 * - 客户端：ctx 为 null
 *
 * @param {Object|null} eggCtx - 服务端传入 Egg.js ctx，客户端传 null
 * @param {Object} [clientParams] - 客户端参数 { query, params }，服务端忽略
 * @returns {Object} 统一的 context 对象
 */
function createUnifiedContext(eggCtx, clientParams) {
  var isServerEnv = !!eggCtx && typeof window === 'undefined';

  if (isServerEnv) {
    return {
      cookies: {
        /**
         * 获取 cookie
         * @param {string} name - cookie 名称
         * @param {Object} [options] - 选项，如 { signed: false, encrypt: false }
         * @returns {string|null} cookie 值
         */
        get: function(name, options) {
          return eggCtx.cookies.get(name, options);
        },
        /**
         * 设置 cookie
         * @param {string} name - cookie 名称
         * @param {string} value - cookie 值
         * @param {Object} [options] - 选项，如 { maxAge, path, domain, httpOnly, secure, signed, sameSite, overwrite }
         */
        set: function(name, value, options) {
          eggCtx.cookies.set(name, value, options);
        }
      },
      query: eggCtx.query || {},
      params: eggCtx.params || {},
      request: {
        url: (eggCtx.request && eggCtx.request.url) || '',
        path: (eggCtx.request && eggCtx.request.path) || eggCtx.path || '',
        host: (eggCtx.request && eggCtx.request.host) || eggCtx.host || ''
      },
      state: eggCtx.state || {},
      isServer: true,
      isClient: false,
      ctx: eggCtx
    };
  }

  // 客户端
  var params = clientParams || {};
  return {
    cookies: {
      /**
       * 获取 cookie（客户端从 document.cookie 解析）
       * @param {string} name - cookie 名称
       * @param {Object} [options] - 选项（客户端忽略 signed/encrypt）
       * @returns {string|null} cookie 值
       */
      get: function(name, options) {
        if (typeof document === 'undefined') return null;
        var pairs = document.cookie.split('; ');
        for (var i = 0; i < pairs.length; i++) {
          var idx = pairs[i].indexOf('=');
          if (idx === -1) continue;
          var key = pairs[i].substring(0, idx);
          if (key === name) {
            return decodeURIComponent(pairs[i].substring(idx + 1));
          }
        }
        return null;
      },
      /**
       * 设置 cookie（客户端通过 document.cookie 设置）
       * @param {string} name - cookie 名称
       * @param {string} value - cookie 值（传 null 删除 cookie）
       * @param {Object} [options] - 选项 { maxAge, path, domain, secure, sameSite }（httpOnly 客户端不生效）
       */
      set: function(name, value, options) {
        if (typeof document === 'undefined') return;
        options = options || {};
        var cookie = encodeURIComponent(name) + '=';
        if (value === null || value === undefined) {
          // 删除 cookie
          cookie += '; max-age=0';
        } else {
          cookie += encodeURIComponent(value);
          if (options.maxAge !== undefined) {
            // Egg.js maxAge 单位为毫秒，document.cookie max-age 单位为秒
            cookie += '; max-age=' + Math.floor(options.maxAge / 1000);
          }
        }
        cookie += '; path=' + (options.path || '/');
        if (options.domain) cookie += '; domain=' + options.domain;
        if (options.secure) cookie += '; secure';
        if (options.sameSite) cookie += '; samesite=' + options.sameSite;
        document.cookie = cookie;
      }
    },
    query: params.query || {},
    params: params.params || {},
    request: {
      url: typeof window !== 'undefined' ? window.location.href : '',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      host: typeof window !== 'undefined' ? window.location.host : ''
    },
    state: {},
    isServer: false,
    isClient: true,
    ctx: null
  };
}

/**
 * 统一创建请求函数（SSR 和 CSR 共用同一入口）
 *
 * 返回的函数签名: async (config) => [err, data]
 *
 * - 服务端：使用 eggCtx.$ajax（BaseController 绑定的 request.js）
 * - 客户端：使用 c_ajax（common/utils.js 的 Axios 封装）
 *
 * @param {Object} unifiedContext - createUnifiedContext 创建的统一上下文
 * @returns {Function} async (config) => [err, data]
 */
function createUnifiedRequest(unifiedContext) {
  if (unifiedContext.isServer) {
    var eggCtx = unifiedContext.ctx;
    return function serverRequest(config) {
      return eggCtx.$ajax(config).then(function(res) {
        return normalizeResponse(res);
      }).catch(function(e) {
        return [e, null];
      });
    };
  }

  // 客户端：延迟加载 c_ajax，仅在 webpack 环境执行
  var c_ajax = require('../../common/utils').c_ajax;
  return function clientRequest(config) {
    return c_ajax(config).then(function(res) {
      return normalizeResponse(res);
    }).catch(function(e) {
      return [e, null];
    });
  };
}

/**
 * 创建数据加载器 - 用于客户端
 * @param {Function} fetchData - 数据获取函数，签名: async (context, request) => data
 * @param {Object} options - 配置选项
 * @param {boolean} options.showLoading - 是否显示加载状态，默认 true
 * @param {Function} options.onError - 错误处理回调
 * @returns {Object} 包含 load 方法的对象
 */
function createDataLoader(fetchData, options) {
  options = options || {};
  var showLoading = options.showLoading !== undefined ? options.showLoading : true;
  var onError = options.onError || null;

  return {
    /**
     * 加载数据
     * @param {Object} params - 请求参数 { query, params }
     * @returns {Promise<Object>} { success, data, error }
     */
    async load(params) {
      params = params || {};
      try {
        // 通过统一函数创建上下文和请求（客户端 eggCtx 为 null）
        var unifiedContext = createUnifiedContext(null, params);
        var request = createUnifiedRequest(unifiedContext);
        var data = await fetchData(unifiedContext, request);

        return {
          success: true,
          data: data,
          error: null
        };
      } catch (error) {
        console.error('Data loading error:', error);

        if (onError) {
          onError(error);
        }

        return {
          success: false,
          data: null,
          error: error.message || 'Failed to load data'
        };
      }
    }
  };
}

/**
 * 服务端调用 fetchData 的包装函数
 * @param {Object} ctx - Egg.js context
 * @param {Function} fetchData - 数据获取函数
 * @param {Object} options - 配置选项
 * @returns {Promise<Object>} { success, data, error }
 */
async function loadDataOnServer(ctx, fetchData, options) {
  options = options || {};
  var fallbackToCSR = options.fallbackToCSR !== undefined ? options.fallbackToCSR : true;
  var useDefaultData = options.useDefaultData !== undefined ? options.useDefaultData : null;
  var throwError = options.throwError || false;

  try {
    // 通过统一函数创建上下文和请求（服务端传入 Egg ctx）
    var unifiedContext = createUnifiedContext(ctx);
    var request = createUnifiedRequest(unifiedContext);
    var data = await fetchData(unifiedContext, request);

    return {
      success: true,
      data: data,
      error: null
    };
  } catch (error) {
    ctx.logger.error('SSR data loading error:', error);

    if (throwError) {
      throw error;
    }

    if (useDefaultData !== null) {
      return {
        success: true,
        data: useDefaultData,
        error: null
      };
    }

    if (fallbackToCSR) {
      return {
        success: false,
        data: null,
        error: error.message,
        fallbackToCSR: true
      };
    }

    return {
      success: false,
      data: null,
      error: error.message
    };
  }
}

/**
 * 通用的页面数据获取 API 处理器
 * 用于客户端通过 API 调用服务端的 fetchData 函数
 * @param {Object} ctx - Egg.js context
 * @param {Function} fetchData - 数据获取函数
 */
async function handlePageDataAPI(ctx, fetchData) {
  const result = await loadDataOnServer(ctx, fetchData, {
    fallbackToCSR: false,
    throwError: false
  });

  ctx.body = result;
}

module.exports = {
  createDataLoader,
  createUnifiedContext,
  createUnifiedRequest,
  normalizeResponse,
  loadDataOnServer,
  handlePageDataAPI
};
